// Working API integration for currently enabled endpoints
// This service handles the 4 working APIs: health, fleet, dashboard, market

import { createClient } from '@supabase/supabase-js'

// Use custom domain in production, fallback to direct URL for development
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://ciqowbqbtaluohujwiil.supabase.co'
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNpcW93YnFidGFsdW9odWp3aWlsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjIwNjMwMjMsImV4cCI6MjA3NzYzOTAyM30.NE2zDKTw7IaaYZm_xx4Bed-wqEhlP9pRtPwaL0yRVHI'

export interface ApiResponse<T> {
  data?: T
  error?: string
  status: number
}

export interface HealthStatus {
  status: string
  timestamp: string
  uptime?: number
  systems?: {
    database: string
    functions: string
    auth: string
  }
}

export interface FleetData {
  fleets: Array<{
    id: string
    name: string
    ships: number
    power: number
    status: string
    location: string
    destination?: string
    eta?: string
  }>
  total_power: number
  active_missions: number
}

export interface DashboardData {
  player: {
    name: string
    level: number
    credits: number
    experience: number
  }
  resources: {
    crystals: number
    metals: number
    energy: number
    alloys: number
  }
  activity: Array<{
    id: string
    type: string
    description: string
    timestamp: string
  }>
  notifications: Array<{
    id: string
    type: 'info' | 'warning' | 'success' | 'error'
    message: string
    timestamp: string
    read: boolean
  }>
}

export interface MarketData {
  listings: Array<{
    id: string
    item_type: string
    quantity: number
    price: number
    seller: string
    location: string
    expires_at: string
  }>
  player_listings: Array<{
    id: string
    item_type: string
    quantity: number
    price: number
    status: string
  }>
  market_stats: {
    total_volume: number
    active_listings: number
    avg_prices: Record<string, number>
  }
}

class WorkingAPI {
  private static instance: WorkingAPI
  private supabase
  
  constructor() {
    this.supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  }

  static getInstance(): WorkingAPI {
    if (!WorkingAPI.instance) {
      WorkingAPI.instance = new WorkingAPI()
    }
    return WorkingAPI.instance
  }

  private async callFunction<T>(
    functionName: string, 
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const { data: result, error } = await this.supabase.functions.invoke(
        functionName, 
        { 
          body: data,
          method
        }
      )

      if (error) {
        return {
          error: error.message,
          status: 400
        }
      }

      // Check if the function is disabled
      if (result && typeof result === 'object' && 'error' in result) {
        if (result.error.includes('functionality is currently disabled')) {
          return {
            error: `${functionName} is currently disabled`,
            status: 503
          }
        }
      }

      return {
        data: result,
        status: 200
      }
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Network error',
        status: 0
      }
    }
  }

  // ===========================================
  // HEALTH MONITORING
  // ===========================================
  
  async getHealth(): Promise<ApiResponse<HealthStatus>> {
    return this.callFunction<HealthStatus>('health', 'GET')
  }

  // ===========================================
  // FLEET MANAGEMENT
  // ===========================================

  async getFleetData(): Promise<ApiResponse<FleetData>> {
    return this.callFunction<FleetData>('fleet', 'GET')
  }

  async deployFleet(fleetId: string, destination: string): Promise<ApiResponse<any>> {
    return this.callFunction('fleet', 'POST', {
      action: 'deploy',
      fleet_id: fleetId,
      destination
    })
  }

  async recallFleet(fleetId: string): Promise<ApiResponse<any>> {
    return this.callFunction('fleet', 'POST', {
      action: 'recall',
      fleet_id: fleetId
    })
  }

  // ===========================================
  // DASHBOARD DATA
  // ===========================================

  async getDashboardData(): Promise<ApiResponse<DashboardData>> {
    return this.callFunction<DashboardData>('dashboard', 'GET')
  }

  async markNotificationRead(notificationId: string): Promise<ApiResponse<any>> {
    return this.callFunction('dashboard', 'POST', {
      action: 'mark_notification_read',
      notification_id: notificationId
    })
  }

  // ===========================================
  // MARKET DATA
  // ===========================================

  async getMarketData(): Promise<ApiResponse<MarketData>> {
    return this.callFunction<MarketData>('market', 'GET')
  }

  async createListing(itemType: string, quantity: number, price: number): Promise<ApiResponse<any>> {
    return this.callFunction('market', 'POST', {
      action: 'create_listing',
      item_type: itemType,
      quantity,
      price
    })
  }

  async buyItem(listingId: string, quantity: number): Promise<ApiResponse<any>> {
    return this.callFunction('market', 'POST', {
      action: 'buy',
      listing_id: listingId,
      quantity
    })
  }

  async cancelListing(listingId: string): Promise<ApiResponse<any>> {
    return this.callFunction('market', 'DELETE', {
      listing_id: listingId
    })
  }
}

export default WorkingAPI